// src/components/ImportSourceCard.tsx
// 外部サービスからのCSVインポートの1カード分（ソースごとに独立した状態を持つ）。
// ブクログ・readee等、複数の外部サービスに対応するため ImportBooksPage から共通利用する。

import React, { useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAppTokens } from '../context/ThemeModeContext';
import { fileToBase64, ImportResult, ImportResolveResult, ImportFailedItem, ResolveItem } from '../utils/importApi';
import UnresolvedImportItem from './UnresolvedImportItem';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

interface ImportSourceCardProps {
  title: string;
  description: string;
  supportedItems: string[];
  unsupportedNote: string;
  importFn: (csvBase64: string, idToken: string) => Promise<ImportResult>;
  resolveFn: (items: ResolveItem[], idToken: string) => Promise<ImportResolveResult>;
}

const ImportSourceCard: React.FC<ImportSourceCardProps> = ({
  title,
  description,
  supportedItems,
  unsupportedNote,
  importFn,
  resolveFn,
}) => {
  const TOKENS = useAppTokens();
  const { idToken } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);

  // ISBN未解決だった本を、手動検索で選び直すためのstate
  const [unresolvedFailed, setUnresolvedFailed] = useState<ImportFailedItem[]>([]);
  const [selections, setSelections] = useState<Record<number, number | null>>({});
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [resolveSummary, setResolveSummary] = useState<string | null>(null);

  const cardSx = {
    border: `1px solid ${TOKENS.border}`,
    background: TOKENS.glass,
    borderRadius: '18px',
    p: 2.5,
  } as const;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setResult(null);
    setSelectedFile(e.target.files?.[0] ?? null);
  };

  const handleImport = async () => {
    if (!selectedFile || !idToken) return;
    setImporting(true);
    setError(null);
    setResult(null);
    setUnresolvedFailed([]);
    setSelections({});
    setResolveSummary(null);
    setResolveError(null);
    try {
      const base64 = await fileToBase64(selectedFile);
      const res = await importFn(base64, idToken);
      setResult(res);
      setUnresolvedFailed(res.failed);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || 'インポートに失敗しました。CSVファイルの形式を確認してください。');
    } finally {
      setImporting(false);
    }
  };

  const selectedCount = Object.values(selections).filter((v) => v != null).length;
  const hasUnselected = unresolvedFailed.length > selectedCount;

  const handleResolveImport = async () => {
    if (!idToken) return;
    const items = unresolvedFailed
      .map((item, i) => ({ item, rlBookId: selections[i] }))
      .filter((x): x is { item: ImportFailedItem; rlBookId: number } => x.rlBookId != null)
      .map(({ item, rlBookId }) => ({
        rl_book_id: rlBookId,
        category: item.category,
        registered_at: item.registered_at,
        memo: item.memo,
        review: item.review,
      }));

    if (items.length === 0) return;

    setResolving(true);
    setResolveError(null);
    setResolveSummary(null);
    try {
      const res = await resolveFn(items, idToken);
      setResolveSummary(
        `${res.imported}冊を登録しました（重複スキップ ${res.skipped_duplicate}冊、抜粋・メモ ${res.highlights_created}件）`,
      );
      // 登録できた分をリストから取り除く（選択されていた項目のindexを除外）
      const resolvedIndices = new Set(
        unresolvedFailed.map((_, i) => i).filter((i) => selections[i] != null),
      );
      setUnresolvedFailed((prev) => prev.filter((_, i) => !resolvedIndices.has(i)));
      setSelections({});
    } catch (e: any) {
      setResolveError(e?.response?.data?.error?.message || '登録に失敗しました');
    } finally {
      setResolving(false);
    }
  };

  return (
    <Box sx={cardSx}>
      <Typography sx={{ fontSize: 15, fontWeight: 700, color: TOKENS.text, mb: 1.5 }}>{title}</Typography>

      <Box
        sx={{
          background: TOKENS.cardBg,
          border: `1px solid ${TOKENS.border}`,
          borderRadius: '12px',
          p: 2,
          mb: 2.5,
        }}
      >
        <Typography sx={{ fontSize: 12.5, color: TOKENS.text, lineHeight: 1.9 }}>{description}</Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: TOKENS.textSub, mt: 2, mb: 0.75 }}>
          対応するデータ
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5, fontSize: 12, color: TOKENS.textSub, lineHeight: 1.9 }}>
          {supportedItems.map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </Box>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: TOKENS.textSub, mt: 1.5, mb: 0.75 }}>
          対応しないデータ
        </Typography>
        <Typography sx={{ fontSize: 12, color: TOKENS.textSub, lineHeight: 1.9 }}>{unsupportedNote}</Typography>
      </Box>

      <input ref={fileInputRef} type="file" accept=".csv" hidden onChange={handleFileChange} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
        <Button
          onClick={() => fileInputRef.current?.click()}
          startIcon={<UploadFileIcon sx={{ fontSize: 18 }} />}
          disabled={importing}
          sx={{
            borderRadius: '999px',
            textTransform: 'none',
            fontSize: 12.5,
            color: TOKENS.text,
            background: TOKENS.buttonBg,
            border: `1px solid ${TOKENS.borderSoft}`,
            '&:hover': { borderColor: TOKENS.accent, background: TOKENS.buttonBgHover },
          }}
        >
          CSVファイルを選択
        </Button>
        {selectedFile && <Typography sx={{ fontSize: 12, color: TOKENS.textSub }}>{selectedFile.name}</Typography>}
      </Box>

      <Button
        fullWidth
        disabled={!selectedFile || importing}
        onClick={handleImport}
        sx={{
          borderRadius: '999px',
          textTransform: 'none',
          fontWeight: 700,
          py: 1,
          color: TOKENS.bg,
          background: TOKENS.text,
          '&:hover': { background: TOKENS.textSub },
          '&.Mui-disabled': { color: TOKENS.bg, background: TOKENS.borderSoft },
        }}
      >
        {importing ? <CircularProgress size={18} sx={{ color: TOKENS.bg }} /> : 'インポートする'}
      </Button>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {result && (
        <Box sx={{ mt: 2.5, borderTop: `1px solid ${TOKENS.border}`, pt: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 22 }} />
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: TOKENS.text }}>
              インポートが完了しました
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, fontSize: 13, color: TOKENS.text, mb: 1.5 }}>
            <Typography sx={{ fontSize: 13 }}>読み込んだ行数: {result.total_rows}</Typography>
            <Typography sx={{ fontSize: 13, color: '#2e7d32' }}>登録した本: {result.imported}冊</Typography>
            <Typography sx={{ fontSize: 13, color: TOKENS.textSub }}>
              重複のためスキップ: {result.skipped_duplicate}冊
            </Typography>
            <Typography sx={{ fontSize: 13, color: TOKENS.textSub }}>
              抜粋・メモを作成: {result.highlights_created}件
            </Typography>
            {result.shelves_created.length > 0 && (
              <Typography sx={{ fontSize: 13, color: TOKENS.textSub }}>
                新規作成した本棚: {result.shelves_created.join('、')}
              </Typography>
            )}
          </Box>

          {unresolvedFailed.length > 0 && (
            <Box>
              <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: TOKENS.error, mb: 0.5 }}>
                見つからなかった本: {unresolvedFailed.length}冊
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: TOKENS.textSub, mb: 1.5 }}>
                ISBNで自動照合できなかった本です。タイトルで検索し、該当する本を選んでください（候補を1回クリックすると選択候補として表示、もう一度クリックすると選択が確定します）。
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {unresolvedFailed.map((item, i) => (
                  <UnresolvedImportItem
                    key={`${item.isbn13}-${item.title}-${i}`}
                    item={item}
                    onSelectionChange={(rlBookId) => setSelections((prev) => ({ ...prev, [i]: rlBookId }))}
                  />
                ))}
              </Box>

              {hasUnselected && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5 }}>
                  <WarningAmberIcon sx={{ fontSize: 15, color: '#e0a000' }} />
                  <Typography sx={{ fontSize: 11.5, color: '#e0a000' }}>
                    未選択の本があります（選択済み {selectedCount} / {unresolvedFailed.length}冊）
                  </Typography>
                </Box>
              )}

              {resolveError && (
                <Alert severity="error" sx={{ mt: 1.5 }}>
                  {resolveError}
                </Alert>
              )}
              {resolveSummary && (
                <Alert severity="success" sx={{ mt: 1.5 }}>
                  {resolveSummary}
                </Alert>
              )}

              <Button
                fullWidth
                disabled={selectedCount === 0 || resolving}
                onClick={handleResolveImport}
                sx={{
                  mt: 1.5,
                  borderRadius: '999px',
                  textTransform: 'none',
                  fontWeight: 700,
                  py: 1,
                  color: TOKENS.bg,
                  background: TOKENS.text,
                  '&:hover': { background: TOKENS.textSub },
                  '&.Mui-disabled': { color: TOKENS.bg, background: TOKENS.borderSoft },
                }}
              >
                {resolving ? (
                  <CircularProgress size={18} sx={{ color: TOKENS.bg }} />
                ) : (
                  `ISBN情報がなかった本を、上記の選択した本として登録（${selectedCount}冊）`
                )}
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ImportSourceCard;
